import { Grip, Plus, SquarePen, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { AgreementFormRenderer } from "@/components/AgreementFormRenderer";
import {
  CORE_FORM_FIELD_KEYS,
  buildFormLogic,
  createEmptyBlock,
  type AgreementBlockType,
  type AgreementFieldBlock,
  type AgreementFormAttachment,
  type AgreementFormBlock,
  type AgreementFormOption,
  type AgreementFormSchema,
  type AgreementFormSection,
  type AgreementFormValues,
  type AgreementGridMultiBlock,
  type AgreementGridSingleBlock,
  type AgreementSignatureBlock,
  isFieldBlock
} from "@/lib/agreement-form-schema";

const BLOCK_LIBRARY: Array<{
  description: string;
  label: string;
  type: AgreementBlockType;
}> = [
  {
    type: "heading",
    label: "Heading",
    description: "Large read-only title text."
  },
  {
    type: "paragraph",
    label: "Paragraph",
    description: "Simple supporting text block."
  },
  {
    type: "multimodalText",
    label: "Multimodal Text",
    description: "Rich agreement copy with optional image attachments."
  },
  {
    type: "shortText",
    label: "Short Text",
    description: "Single-line input for quick answers."
  },
  {
    type: "longText",
    label: "Long Text",
    description: "Expanded textarea for detailed answers."
  },
  {
    type: "email",
    label: "Email",
    description: "Email input with built-in format validation."
  },
  {
    type: "date",
    label: "Date",
    description: "Date picker for agreement milestones."
  },
  {
    type: "time",
    label: "Time",
    description: "Time input for scheduling flows."
  },
  {
    type: "multipleChoice",
    label: "Multiple Choice",
    description: "Single-selection radio options with section branching."
  },
  {
    type: "checkboxes",
    label: "Checkboxes",
    description: "Multi-select options with count validation."
  },
  {
    type: "dropdown",
    label: "Dropdown",
    description: "Compact single-selection list."
  },
  {
    type: "linearScale",
    label: "Linear Scale",
    description: "Numeric sentiment scale."
  },
  {
    type: "rating",
    label: "Rating",
    description: "Star, heart, or thumb ratings."
  },
  {
    type: "gridSingle",
    label: "Grid Single",
    description: "One selection per row."
  },
  {
    type: "gridMulti",
    label: "Grid Multi",
    description: "Multi-select grid responses."
  },
  {
    type: "signature",
    label: "Signature",
    description: "Signature canvas with required consent checkboxes."
  }
];

const RESERVED_FIELD_KEYS = new Set<string>(Object.values(CORE_FORM_FIELD_KEYS));

interface AgreementFormStudioProps {
  onChange: (schema: AgreementFormSchema) => void;
  previewValues: AgreementFormValues;
  schema: AgreementFormSchema;
}

export function AgreementFormStudio({
  onChange,
  previewValues,
  schema
}: AgreementFormStudioProps) {
  const [selectedSectionId, setSelectedSectionId] = useState(schema.sections[0]?.id ?? "");
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(schema.sections[0]?.blocks[0]?.id ?? null);
  const hasNoSections = schema.sections.length === 0;

  useEffect(() => {
    if (!schema.sections.some((section) => section.id === selectedSectionId)) {
      setSelectedSectionId(schema.sections[0]?.id ?? "");
    }
  }, [schema.sections, selectedSectionId]);

  const selectedSection = useMemo(
    () => schema.sections.find((section) => section.id === selectedSectionId) ?? schema.sections[0],
    [schema.sections, selectedSectionId]
  );

  useEffect(() => {
    if (!selectedSection) {
      setSelectedBlockId(null);
      return;
    }

    if (selectedBlockId && selectedSection.blocks.some((block) => block.id === selectedBlockId)) {
      return;
    }

    setSelectedBlockId(selectedSection.blocks[0]?.id ?? null);
  }, [selectedBlockId, selectedSection]);

  const selectedBlock = selectedSection?.blocks.find((block) => block.id === selectedBlockId) ?? null;

  function commit(nextSections: AgreementFormSection[]) {
    onChange({
      ...schema,
      sections: nextSections,
      logic: buildFormLogic(nextSections)
    });
  }

  function updateSchemaMeta(field: "description" | "title", value: string) {
    onChange({
      ...schema,
      [field]: value
    });
  }

  function updateSection(sectionId: string, updater: (section: AgreementFormSection) => AgreementFormSection) {
    commit(schema.sections.map((section) => (section.id === sectionId ? updater(section) : section)));
  }

  function updateBlock(
    sectionId: string,
    blockId: string,
    updater: (block: AgreementFormBlock) => AgreementFormBlock
  ) {
    updateSection(sectionId, (section) => ({
      ...section,
      blocks: section.blocks.map((block) => (block.id === blockId ? updater(block) : block))
    }));
  }

  function createSectionDraft(initialBlock?: AgreementFormBlock): AgreementFormSection {
    return {
      id: `section-${Math.random().toString(36).slice(2, 9)}`,
      title: `Section ${schema.sections.length + 1}`,
      description: "Group related questions together in this section.",
      blocks: initialBlock ? [initialBlock] : []
    };
  }

  function addSection() {
    const nextSection = createSectionDraft();

    commit([...schema.sections, nextSection]);
    setSelectedSectionId(nextSection.id);
    setSelectedBlockId(null);
  }

  function addBlock(type: AgreementBlockType) {
    const nextBlock = createEmptyBlock(type);

    if (!selectedSection) {
      const nextSection = createSectionDraft(nextBlock);
      commit([...schema.sections, nextSection]);
      setSelectedSectionId(nextSection.id);
      setSelectedBlockId(nextBlock.id);
      return;
    }

    updateSection(selectedSection.id, (section) => ({
      ...section,
      blocks: [...section.blocks, nextBlock]
    }));
    setSelectedBlockId(nextBlock.id);
  }

  function moveSection(sectionId: string, direction: -1 | 1) {
    const currentIndex = schema.sections.findIndex((section) => section.id === sectionId);
    const nextIndex = currentIndex + direction;

    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= schema.sections.length) {
      return;
    }

    const nextSections = [...schema.sections];
    const [section] = nextSections.splice(currentIndex, 1);
    nextSections.splice(nextIndex, 0, section);
    commit(nextSections);
  }

  function moveBlock(sectionId: string, blockId: string, direction: -1 | 1) {
    updateSection(sectionId, (section) => {
      const currentIndex = section.blocks.findIndex((block) => block.id === blockId);
      const nextIndex = currentIndex + direction;

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= section.blocks.length) {
        return section;
      }

      const nextBlocks = [...section.blocks];
      const [block] = nextBlocks.splice(currentIndex, 1);
      nextBlocks.splice(nextIndex, 0, block);
      return {
        ...section,
        blocks: nextBlocks
      };
    });
  }

  function removeSection(sectionId: string) {
    if (schema.sections.length <= 1) {
      return;
    }

    const section = schema.sections.find((entry) => entry.id === sectionId);

    if (!section || section.blocks.some(isProtectedBlock)) {
      return;
    }

    const nextSections = schema.sections.filter((entry) => entry.id !== sectionId);
    commit(nextSections);
    setSelectedSectionId(nextSections[0]?.id ?? "");
  }

  function removeBlock(sectionId: string, blockId: string) {
    const block = schema.sections
      .find((section) => section.id === sectionId)
      ?.blocks.find((entry) => entry.id === blockId);

    if (!block || isProtectedBlock(block)) {
      return;
    }

    updateSection(sectionId, (section) => ({
      ...section,
      blocks: section.blocks.filter((entry) => entry.id !== blockId)
    }));
    setSelectedBlockId(null);
  }

  return (
    <div className="space-y-6">
      <section className="surface p-6">
        <div className="grid gap-4 lg:grid-cols-2">
          <label className="block space-y-2.5">
            <span className="field-label">Candidate form name</span>
            <input
              className="input-field"
              onChange={(event) => updateSchemaMeta("title", event.target.value)}
              value={schema.title}
            />
          </label>
          <label className="block space-y-2.5">
            <span className="field-label">Candidate form description</span>
            <input
              className="input-field"
              onChange={(event) => updateSchemaMeta("description", event.target.value)}
              value={schema.description ?? ""}
            />
          </label>
        </div>
      </section>

      <section className="rounded-[24px] border border-line/80 bg-gradient-to-r from-[#f8fbff] via-white to-[#f3f8f2] p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">Start Here</p>
        <h3 className="mt-2 text-xl font-semibold tracking-tight text-ink">
          Build the candidate questions in three simple steps
        </h3>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <StudioGuideStep
            description="Create the first section for a group of related questions."
            step="1"
            title="Add a section"
          />
          <StudioGuideStep
            description="Pick the block type you want, like short text, dropdown, date, or signature."
            step="2"
            title="Add fields or content"
          />
          <StudioGuideStep
            description="Click any section or block to change labels, options, and validation on the right."
            step="3"
            title="Edit settings"
          />
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[280px_minmax(0,1fr)_360px]">
        <div className="surface p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="eyebrow">Step 1: Add Questions</p>
              <h3 className="mt-2 text-xl font-semibold tracking-tight text-ink">Pick what to add</h3>
              <p className="mt-2 text-sm leading-6 text-slate">
                Use Section to start a question group, or choose a question type and we will
                create the first section for you automatically.
              </p>
            </div>
            <button className="btn-secondary px-4 py-2.5" onClick={addSection} type="button">
              <Plus className="h-4 w-4" />
              Section
            </button>
          </div>

          <div className="mt-5 grid gap-3">
            {BLOCK_LIBRARY.map((item) => (
              <button
                className="rounded-[22px] border border-line/70 bg-cloud/55 px-4 py-4 text-left transition hover:bg-white"
                key={item.type}
                onClick={() => addBlock(item.type)}
                type="button"
              >
                <p className="text-sm font-semibold text-ink">{item.label}</p>
                <p className="mt-2 text-sm leading-6 text-slate">{item.description}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="surface p-5">
            <p className="eyebrow">Your Form Outline</p>
            <h3 className="mt-2 text-xl font-semibold tracking-tight text-ink">
              {hasNoSections ? "Nothing added yet" : "Questions in candidate order"}
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate">
              Sections appear here in the same order candidates will see them. Click any section
              or question card to edit it in the right panel.
            </p>
          </div>

          {hasNoSections ? (
            <div className="surface p-6">
              <div className="rounded-[24px] border border-dashed border-line bg-cloud/35 px-6 py-8">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">
                  No sections yet
                </p>
                <h3 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
                  Start building your form here
                </h3>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate">
                  Create the first section to organize related questions, or start instantly with
                  a first question and we will create the section for you.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button className="btn-secondary" onClick={addSection} type="button">
                    <Plus className="h-4 w-4" />
                    Create first section
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() => addBlock("shortText")}
                    type="button"
                  >
                    Start with a short text question
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {schema.sections.map((section, sectionIndex) => {
            const sectionSelected = section.id === selectedSection?.id;
            const sectionProtected = section.blocks.some(isProtectedBlock);

            return (
              <div
                className={`surface p-5 transition ${sectionSelected ? "ring-2 ring-royal/15" : ""}`}
                key={section.id}
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <button
                    className="text-left"
                    onClick={() => {
                      setSelectedSectionId(section.id);
                      setSelectedBlockId(section.blocks[0]?.id ?? null);
                    }}
                    type="button"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/70">
                      Section {sectionIndex + 1}
                    </p>
                    <h3 className="mt-2 text-xl font-semibold tracking-tight text-ink">
                      {section.title}
                    </h3>
                    {section.description ? (
                      <p className="mt-2 text-sm leading-6 text-slate">{section.description}</p>
                    ) : null}
                  </button>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="btn-ghost px-3 py-2.5"
                      onClick={() => moveSection(section.id, -1)}
                      type="button"
                    >
                      Up
                    </button>
                    <button
                      className="btn-ghost px-3 py-2.5"
                      onClick={() => moveSection(section.id, 1)}
                      type="button"
                    >
                      Down
                    </button>
                    <button
                      className="btn-danger px-3 py-2.5"
                      disabled={schema.sections.length <= 1 || sectionProtected}
                      onClick={() => removeSection(section.id)}
                      type="button"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </button>
                  </div>
                </div>

                <div className="mt-5 grid gap-3">
                  {section.blocks.length === 0 ? (
                    <div className="rounded-[22px] border border-dashed border-line bg-cloud/45 px-5 py-6 text-sm text-slate">
                      Add blocks from the palette to start building this section.
                    </div>
                  ) : (
                    section.blocks.map((block, blockIndex) => {
                      const blockSelected = block.id === selectedBlock?.id && section.id === selectedSection?.id;

                      return (
                        <div
                          className={`rounded-[22px] border px-4 py-4 transition ${
                            blockSelected
                              ? "border-royal bg-royal/5"
                              : "border-line/70 bg-cloud/55 hover:bg-white"
                          }`}
                          key={block.id}
                        >
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <button
                              className="flex items-start gap-3 text-left"
                              onClick={() => {
                                setSelectedSectionId(section.id);
                                setSelectedBlockId(block.id);
                              }}
                              type="button"
                            >
                              <div className="mt-1 text-slate/70">
                                <Grip className="h-4 w-4" />
                              </div>
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-semibold text-ink">
                                    {getBlockDisplayName(block)}
                                  </span>
                                  <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate/70">
                                    {block.type}
                                  </span>
                                  {isProtectedBlock(block) ? (
                                    <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800">
                                      Core
                                    </span>
                                  ) : null}
                                </div>
                                {block.description ? (
                                  <p className="mt-2 text-sm leading-6 text-slate">{block.description}</p>
                                ) : null}
                              </div>
                            </button>

                            <div className="flex flex-wrap gap-2">
                              <button
                                className="btn-ghost px-3 py-2.5"
                                onClick={() => moveBlock(section.id, block.id, -1)}
                                type="button"
                              >
                                Up
                              </button>
                              <button
                                className="btn-ghost px-3 py-2.5"
                                onClick={() => moveBlock(section.id, block.id, 1)}
                                type="button"
                              >
                                Down
                              </button>
                              <button
                                className="btn-danger px-3 py-2.5"
                                disabled={isProtectedBlock(block)}
                                onClick={() => removeBlock(section.id, block.id)}
                                type="button"
                              >
                                <Trash2 className="h-4 w-4" />
                                Remove
                              </button>
                            </div>
                          </div>

                          <p className="mt-3 text-xs font-medium uppercase tracking-[0.18em] text-slate/70">
                            Block {blockIndex + 1} in this section
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="surface p-5">
          <div>
            <p className="eyebrow">Step 2: Edit Details</p>
            <h3 className="mt-2 text-xl font-semibold tracking-tight text-ink">
              {selectedBlock
                ? "Edit selected question"
                : selectedSection
                  ? "Edit selected section"
                  : "Edit selected item"}
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate">
              Change labels, options, helper text, and validation here. These settings control what
              candidates fill in on the live portal.
            </p>
          </div>

          <div className="mt-5">
            {selectedSection ? (
              selectedBlock ? (
                <BlockPropertiesEditor
                  block={selectedBlock}
                  onChange={(nextBlock) => updateBlock(selectedSection.id, selectedBlock.id, () => nextBlock)}
                  schema={schema}
                />
              ) : (
                <SectionPropertiesEditor
                  onChange={(nextSection) => updateSection(selectedSection.id, () => nextSection)}
                  section={selectedSection}
                />
              )
            ) : (
              <div className="rounded-[22px] border border-dashed border-line bg-cloud/45 px-5 py-6 text-sm text-slate">
                Click a section or question in Your Form Outline to edit it here.
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="surface p-6">
        <div className="flex items-center gap-3">
          <SquarePen className="h-5 w-5 text-royal" />
          <div>
            <p className="eyebrow">Candidate Preview</p>
            <h3 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
              Live candidate experience
            </h3>
          </div>
        </div>

        <p className="mt-3 text-sm leading-7 text-slate">
          This uses the same renderer as the live candidate portal, so the layout, labels, and
          signature block here will match the real experience.
        </p>

        <div className="mt-6">
          <AgreementFormRenderer readOnly schema={schema} values={previewValues} />
        </div>
      </section>
    </div>
  );
}

function StudioGuideStep({
  description,
  step,
  title
}: {
  description: string;
  step: string;
  title: string;
}) {
  return (
    <div className="rounded-[20px] border border-line/80 bg-white/85 p-4">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-navy text-xs font-semibold text-white">
        {step}
      </span>
      <p className="mt-3 text-sm font-semibold text-ink">{title}</p>
      <p className="mt-2 text-sm leading-6 text-slate">{description}</p>
    </div>
  );
}

function SectionPropertiesEditor({
  onChange,
  section
}: {
  onChange: (section: AgreementFormSection) => void;
  section: AgreementFormSection;
}) {
  return (
    <div className="space-y-4">
      <TextControl
        label="Section title"
        onChange={(value) => onChange({ ...section, title: value })}
        value={section.title}
      />
      <TextAreaControl
        label="Section description"
        onChange={(value) => onChange({ ...section, description: value })}
        value={section.description ?? ""}
      />
    </div>
  );
}

function BlockPropertiesEditor({
  block,
  onChange,
  schema
}: {
  block: AgreementFormBlock;
  onChange: (block: AgreementFormBlock) => void;
  schema: AgreementFormSchema;
}) {
  if (block.type === "heading") {
    return (
      <TextAreaControl
        label="Heading text"
        onChange={(value) => onChange({ ...block, text: value })}
        value={block.text}
      />
    );
  }

  if (block.type === "paragraph") {
    return (
      <TextAreaControl
        label="Paragraph text"
        onChange={(value) => onChange({ ...block, text: value })}
        value={block.text}
      />
    );
  }

  if (block.type === "multimodalText") {
    return (
      <div className="space-y-4">
        <TextAreaControl
          label="Rich text HTML"
          onChange={(value) => onChange({ ...block, richTextHtml: value })}
          value={block.richTextHtml}
        />
        <AttachmentEditor
          attachments={block.attachments}
          onChange={(attachments) => onChange({ ...block, attachments })}
        />
      </div>
    );
  }

  if (block.type === "signature") {
    return (
      <SignaturePropertiesEditor
        block={block}
        onChange={(nextBlock) => onChange(nextBlock)}
      />
    );
  }

  return (
    <FieldPropertiesEditor
      block={block}
      onChange={(nextBlock) => onChange(nextBlock)}
      schema={schema}
    />
  );
}

function SignaturePropertiesEditor({
  block,
  onChange
}: {
  block: AgreementSignatureBlock;
  onChange: (block: AgreementSignatureBlock) => void;
}) {
  return (
    <div className="space-y-4">
      <TextControl
        label="Block title"
        onChange={(value) => onChange({ ...block, title: value })}
        value={block.title}
      />
      <TextAreaControl
        label="Helper text"
        onChange={(value) => onChange({ ...block, helperText: value })}
        value={block.helperText ?? ""}
      />
      <TextControl
        label="Signature name label"
        onChange={(value) => onChange({ ...block, nameLabel: value })}
        value={block.nameLabel}
      />
      <TextControl
        label="Location label"
        onChange={(value) => onChange({ ...block, locationLabel: value })}
        value={block.locationLabel}
      />
      <TextControl
        label="Signed date label"
        onChange={(value) => onChange({ ...block, signedAtLabel: value })}
        value={block.signedAtLabel}
      />
      <TextAreaControl
        label="Terms checkbox label"
        onChange={(value) => onChange({ ...block, acceptedTermsLabel: value })}
        value={block.acceptedTermsLabel}
      />
      <TextAreaControl
        label="Confidentiality checkbox label"
        onChange={(value) => onChange({ ...block, acceptedConfidentialityLabel: value })}
        value={block.acceptedConfidentialityLabel}
      />
      <TextAreaControl
        label="Electronic signature checkbox label"
        onChange={(value) => onChange({ ...block, acceptedElectronicSignatureLabel: value })}
        value={block.acceptedElectronicSignatureLabel}
      />
    </div>
  );
}

function FieldPropertiesEditor({
  block,
  onChange,
  schema
}: {
  block: AgreementFieldBlock;
  onChange: (block: AgreementFieldBlock) => void;
  schema: AgreementFormSchema;
}) {
  const protectedFieldKey = RESERVED_FIELD_KEYS.has(block.fieldKey);

  return (
    <div className="space-y-4">
      <TextControl
        label="Label"
        onChange={(value) => onChange({ ...block, label: value })}
        value={block.label}
      />
      <TextAreaControl
        label="Description"
        onChange={(value) => onChange({ ...block, description: value })}
        value={block.description ?? ""}
      />
      <TextControl
        disabled={protectedFieldKey}
        label="Field key"
        note={protectedFieldKey ? "Core field keys are locked to preserve the current submission pipeline." : undefined}
        onChange={(value) => onChange({ ...block, fieldKey: value })}
        value={block.fieldKey}
      />
      <TextControl
        label="Helper text"
        onChange={(value) => onChange({ ...block, helperText: value })}
        value={block.helperText ?? ""}
      />
      <ToggleControl
        checked={Boolean(block.required)}
        label="Required field"
        onChange={(checked) => onChange({ ...block, required: checked })}
      />

      {("placeholder" in block || "defaultValue" in block) && (
        <div className="grid gap-4">
          {"placeholder" in block ? (
            <TextControl
              label="Placeholder"
              onChange={(value) => onChange({ ...block, placeholder: value } as AgreementFieldBlock)}
              value={block.placeholder ?? ""}
            />
          ) : null}
          {"defaultValue" in block ? (
            <TextControl
              label="Default value"
              onChange={(value) => onChange({ ...block, defaultValue: value } as AgreementFieldBlock)}
              value={
                typeof block.defaultValue === "number"
                  ? String(block.defaultValue)
                  : Array.isArray(block.defaultValue)
                    ? block.defaultValue.join(", ")
                    : block.defaultValue ?? ""
              }
            />
          ) : null}
        </div>
      )}

      {(block.type === "multipleChoice" || block.type === "checkboxes" || block.type === "dropdown") ? (
        <OptionEditor
          options={block.options}
          sectionOptions={schema.sections}
          onChange={(options) => onChange({ ...block, options })}
        />
      ) : null}

      {block.type === "linearScale" ? (
        <div className="grid gap-4 md:grid-cols-2">
          <NumberControl
            label="Minimum value"
            onChange={(value) => onChange({ ...block, min: value })}
            value={block.min}
          />
          <NumberControl
            label="Maximum value"
            onChange={(value) => onChange({ ...block, max: value })}
            value={block.max}
          />
          <TextControl
            label="Minimum label"
            onChange={(value) => onChange({ ...block, minLabel: value })}
            value={block.minLabel ?? ""}
          />
          <TextControl
            label="Maximum label"
            onChange={(value) => onChange({ ...block, maxLabel: value })}
            value={block.maxLabel ?? ""}
          />
        </div>
      ) : null}

      {block.type === "rating" ? (
        <div className="grid gap-4 md:grid-cols-2">
          <NumberControl
            label="Maximum icons"
            onChange={(value) => onChange({ ...block, max: value })}
            value={block.max}
          />
          <label className="block space-y-2.5">
            <span className="field-label">Icon style</span>
            <select
              className="input-field"
              onChange={(event) =>
                onChange({ ...block, icon: event.target.value as "heart" | "star" | "thumb" })
              }
              value={block.icon ?? "star"}
            >
              <option value="star">Star</option>
              <option value="heart">Heart</option>
              <option value="thumb">Thumb</option>
            </select>
          </label>
        </div>
      ) : null}

      {(block.type === "gridSingle" || block.type === "gridMulti") ? (
        <GridEditor
          block={block}
          onChange={(nextBlock) => onChange(nextBlock)}
        />
      ) : null}

      <ValidationEditor block={block} onChange={onChange} />
    </div>
  );
}

function AttachmentEditor({
  attachments,
  onChange
}: {
  attachments: AgreementFormAttachment[];
  onChange: (attachments: AgreementFormAttachment[]) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="field-label">Attachments</span>
        <button
          className="btn-secondary px-4 py-2.5"
          onClick={() =>
            onChange([
              ...attachments,
              {
                id: `asset-${Math.random().toString(36).slice(2, 9)}`,
                kind: "image",
                url: "",
                alt: "Agreement image"
              }
            ])
          }
          type="button"
        >
          <Plus className="h-4 w-4" />
          Add Image
        </button>
      </div>

      {attachments.map((attachment) => (
        <div className="rounded-[22px] border border-line/70 bg-cloud/50 p-4" key={attachment.id}>
          <div className="grid gap-3">
            <TextControl
              label="Image URL"
              onChange={(value) =>
                onChange(
                  attachments.map((entry) => (entry.id === attachment.id ? { ...entry, url: value } : entry))
                )
              }
              value={attachment.url}
            />
            <TextControl
              label="Alt text"
              onChange={(value) =>
                onChange(
                  attachments.map((entry) => (entry.id === attachment.id ? { ...entry, alt: value } : entry))
                )
              }
              value={attachment.alt}
            />
            <TextControl
              label="Caption"
              onChange={(value) =>
                onChange(
                  attachments.map((entry) =>
                    entry.id === attachment.id ? { ...entry, caption: value || undefined } : entry
                  )
                )
              }
              value={attachment.caption ?? ""}
            />
            <button
              className="btn-danger w-fit px-4 py-2.5"
              onClick={() => onChange(attachments.filter((entry) => entry.id !== attachment.id))}
              type="button"
            >
              <Trash2 className="h-4 w-4" />
              Remove Image
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function OptionEditor({
  onChange,
  options,
  sectionOptions
}: {
  onChange: (options: AgreementFormOption[]) => void;
  options: AgreementFormOption[];
  sectionOptions: AgreementFormSection[];
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="field-label">Options</span>
        <button
          className="btn-secondary px-4 py-2.5"
          onClick={() =>
            onChange([
              ...options,
              {
                id: `option-${Math.random().toString(36).slice(2, 9)}`,
                label: `Option ${options.length + 1}`,
                value: `option_${options.length + 1}`
              }
            ])
          }
          type="button"
        >
          <Plus className="h-4 w-4" />
          Add Option
        </button>
      </div>

      {options.map((option) => (
        <div className="rounded-[22px] border border-line/70 bg-cloud/50 p-4" key={option.id}>
          <div className="grid gap-3">
            <TextControl
              label="Option label"
              onChange={(value) =>
                onChange(options.map((entry) => (entry.id === option.id ? { ...entry, label: value } : entry)))
              }
              value={option.label}
            />
            <TextControl
              label="Option value"
              onChange={(value) =>
                onChange(options.map((entry) => (entry.id === option.id ? { ...entry, value: value } : entry)))
              }
              value={option.value}
            />
            <TextControl
              label="Description"
              onChange={(value) =>
                onChange(
                  options.map((entry) =>
                    entry.id === option.id ? { ...entry, description: value || undefined } : entry
                  )
                )
              }
              value={option.description ?? ""}
            />
            <label className="block space-y-2.5">
              <span className="field-label">Go to section</span>
              <select
                className="input-field"
                onChange={(event) =>
                  onChange(
                    options.map((entry) =>
                      entry.id === option.id
                        ? {
                            ...entry,
                            goToSectionId: event.target.value || undefined
                          }
                        : entry
                    )
                  )
                }
                value={option.goToSectionId ?? ""}
              >
                <option value="">Default next section</option>
                {sectionOptions.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.title}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="btn-danger w-fit px-4 py-2.5"
              onClick={() => onChange(options.filter((entry) => entry.id !== option.id))}
              type="button"
            >
              <Trash2 className="h-4 w-4" />
              Remove Option
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function GridEditor({
  block,
  onChange
}: {
  block: AgreementGridSingleBlock | AgreementGridMultiBlock;
  onChange: (block: AgreementGridSingleBlock | AgreementGridMultiBlock) => void;
}) {
  return (
    <div className="space-y-4">
      <SimpleListEditor
        items={block.rows}
        label="Rows"
        onChange={(rows) => onChange({ ...block, rows })}
      />
      <SimpleListEditor
        items={block.columns}
        label="Columns"
        onChange={(columns) => onChange({ ...block, columns })}
      />
    </div>
  );
}

function SimpleListEditor({
  items,
  label,
  onChange
}: {
  items: Array<{ id: string; label: string }>;
  label: string;
  onChange: (items: Array<{ id: string; label: string }>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="field-label">{label}</span>
        <button
          className="btn-secondary px-4 py-2.5"
          onClick={() =>
            onChange([
              ...items,
              {
                id: `${label.toLowerCase()}-${Math.random().toString(36).slice(2, 9)}`,
                label: `${label.slice(0, -1)} ${items.length + 1}`
              }
            ])
          }
          type="button"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      {items.map((item) => (
        <div className="flex items-center gap-3" key={item.id}>
          <input
            className="input-field"
            onChange={(event) =>
              onChange(items.map((entry) => (entry.id === item.id ? { ...entry, label: event.target.value } : entry)))
            }
            value={item.label}
          />
          <button
            className="btn-danger px-4 py-2.5"
            onClick={() => onChange(items.filter((entry) => entry.id !== item.id))}
            type="button"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

function ValidationEditor({
  block,
  onChange
}: {
  block: AgreementFieldBlock;
  onChange: (block: AgreementFieldBlock) => void;
}) {
  const validation = block.validation ?? {};

  return (
    <div className="rounded-[22px] border border-line/70 bg-cloud/50 p-4">
      <p className="field-label">Validation</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <NumberControl
          label="Min length / value"
          onChange={(value) => onChange({ ...block, validation: { ...validation, minLength: value, min: value } })}
          value={validation.minLength ?? validation.min ?? 0}
        />
        <NumberControl
          label="Max length / value"
          onChange={(value) => onChange({ ...block, validation: { ...validation, maxLength: value, max: value } })}
          value={validation.maxLength ?? validation.max ?? 0}
        />
        {(block.type === "checkboxes") ? (
          <>
            <NumberControl
              label="Min selected"
              onChange={(value) => onChange({ ...block, validation: { ...validation, minSelected: value } })}
              value={validation.minSelected ?? 0}
            />
            <NumberControl
              label="Max selected"
              onChange={(value) => onChange({ ...block, validation: { ...validation, maxSelected: value } })}
              value={validation.maxSelected ?? 0}
            />
          </>
        ) : null}
      </div>
      {(block.type === "shortText" || block.type === "longText" || block.type === "email") ? (
        <div className="mt-4">
          <TextControl
            label="Regex pattern"
            onChange={(value) => onChange({ ...block, validation: { ...validation, pattern: value || undefined } })}
            value={validation.pattern ?? ""}
          />
        </div>
      ) : null}
      <div className="mt-4">
        <TextAreaControl
          label="Custom error text"
          onChange={(value) => onChange({ ...block, validation: { ...validation, errorText: value || undefined } })}
          value={validation.errorText ?? ""}
        />
      </div>
    </div>
  );
}

function TextControl({
  disabled = false,
  label,
  note,
  onChange,
  value
}: {
  disabled?: boolean;
  label: string;
  note?: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className="block space-y-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="field-label">{label}</span>
        {note ? <span className="field-note">{note}</span> : null}
      </div>
      <input className="input-field" disabled={disabled} onChange={(event) => onChange(event.target.value)} value={value} />
    </label>
  );
}

function TextAreaControl({
  label,
  onChange,
  value
}: {
  label: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <label className="block space-y-2.5">
      <span className="field-label">{label}</span>
      <textarea className="input-field min-h-28" onChange={(event) => onChange(event.target.value)} value={value} />
    </label>
  );
}

function NumberControl({
  label,
  onChange,
  value
}: {
  label: string;
  onChange: (value: number) => void;
  value: number;
}) {
  return (
    <label className="block space-y-2.5">
      <span className="field-label">{label}</span>
      <input
        className="input-field"
        min={0}
        onChange={(event) => onChange(Number(event.target.value || 0))}
        type="number"
        value={value}
      />
    </label>
  );
}

function ToggleControl({
  checked,
  label,
  onChange
}: {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-[22px] border border-line/70 bg-cloud/50 px-4 py-4">
      <input
        checked={checked}
        className="mt-1 h-4 w-4 rounded border-line"
        onChange={(event) => onChange(event.target.checked)}
        type="checkbox"
      />
      <span className="text-sm leading-7 text-slate">{label}</span>
    </label>
  );
}

function getBlockDisplayName(block: AgreementFormBlock) {
  if (block.type === "heading" || block.type === "paragraph") {
    return block.text;
  }

  if (block.type === "multimodalText") {
    return "Multimodal agreement content";
  }

  if (block.type === "signature") {
    return block.title;
  }

  return block.label;
}

function isProtectedBlock(block: AgreementFormBlock) {
  if (block.type === "signature") {
    return true;
  }

  return isFieldBlock(block) && RESERVED_FIELD_KEYS.has(block.fieldKey);
}
